import { useQuery } from "@tanstack/react-query";

import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { realtimeTopic } from "@/lib/realtime/channel";

import { listMyOpenAssignedIssues } from "@/stores/pm/repository";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { visibleStatuses } from "@/domains/pm/statuses/statusCatalogue";
import type { MyAssignedIssue } from "@/stores/pm/repository";

export type { MyAssignedIssue };

/**
 * The current user's active (`in_progress`) issues in one project — or across
 * the whole building when `projectId` is null ("All projects" scope) — for
 * the project dashboard's "My tasks" widget. The due-date window is applied
 * client-side by the widget so changing the window never refetches.
 */
export function useMyOpenAssignedIssues(
  buildingId: number,
  projectId: number | null,
  userId: number | undefined
) {
  const { catalogue } = useStatusCatalogue();
  // Which statuses still count as the user's work. Part of the key, so
  // defining a new open status refreshes the widget rather than leaving it
  // quietly short.
  const openStatuses = visibleStatuses(catalogue, projectId)
    .filter((d) => d.category === "open")
    .map((d) => d.key);

  // Closing a task on the Issues page left this badge counting it — the key
  // was never invalidated anywhere, so on an open dashboard it never recovered.
  //
  // Two bindings, because the query joins two tables. `pm_issues` covers status
  // and due-date edits; assignment and unassignment write only the junction
  // `pm_issue_assignees`, which emits nothing here — that table is not in the
  // `supabase_realtime` publication, so a binding on it would be silently dead.
  // Its `assignee_added` / `assignee_removed` rows in `pm_issue_activity` are
  // published, carry `building_id`, and are what the widget actually needs.
  useRealtimeInvalidate({
    enabled: buildingId > 0 && userId != null,
    topic: realtimeTopic("pm-my-open-issues", buildingId, userId),
    bindings: [
      { event: "*", table: "pm_issues", filter: `building_id=eq.${buildingId}` },
      { event: "INSERT", table: "pm_issue_activity", filter: `building_id=eq.${buildingId}` },
    ],
    invalidate: [{ queryKey: ["pm-my-open-issues", buildingId] }],
  });

  return useQuery<MyAssignedIssue[]>({
    queryKey: ["pm-my-open-issues", buildingId, projectId, userId, openStatuses.join(",")],
    enabled: buildingId > 0 && userId != null,
    staleTime: 60_000,
    queryFn: () => listMyOpenAssignedIssues(buildingId, projectId, userId as number, openStatuses),
  });
}
